import { useMemo } from 'react'
import { useSelector } from 'react-redux'
import dayjs from 'dayjs'
import Button from '../../../components/Button/Button'
import Calendar from '../../../components/Calendar/Calendar'
import Dropdown from '../../../components/Dropdown/Dropdown'
import { calculateMonthCount } from '../../Contacts/add-contact/helper'
import { selectFeatureFlag } from '../../../features/featureFlags/selectors'
import { DEBT_AMOUNT_DECIMALS } from '../../../config/featureFlagKeys'
import { formatLocaleAmount } from '../../../utils/amount'
import { formatMinimumPayment, getFDRMinimumPayment, getOnePercentFloor } from '../../../config/consts'

const frequencyOptions = [
  { value: 'monthly', label: 'Monthly' },
  { value: 'biweekly', label: 'Bi-Weekly' },
]

const TermSelectionStep = ({
  config,
  selectedDebts,
  selectedTerm,
  setSelectedTerm,
  frequency,
  setFrequency,
  startDate,
  setStartDate,
  onNext,
  onBack,
}) => {
  const amountDecimals = useSelector(selectFeatureFlag(DEBT_AMOUNT_DECIMALS))
  const totalDebt = selectedDebts.reduce((sum, d) => sum + (d.balanceAmount || 0), 0)
  const onePercentFloor = config.onePercentFloor ? getOnePercentFloor(totalDebt) : 0
  const onePercentMinimum = onePercentFloor > getFDRMinimumPayment(totalDebt) ? onePercentFloor : 0

  const termOptions = useMemo(() => {
    return config.allowedTerms.map(term => {
      const programFeeAmount = totalDebt * (config.programFee / 100)
      const numberOfPayments = frequency === 'biweekly' ? term * 2 : term
      const monthCount = calculateMonthCount({
        programLength: numberOfPayments,
        frequency,
        startDate,
      })
      const custodialTotal = monthCount === null ? null : (config.custodialFee || 0) * monthCount
      const totalBalance = custodialTotal === null ? null : programFeeAmount + custodialTotal
      const paymentAmount = totalBalance === null ? null : totalBalance / numberOfPayments
      const monthlyEquivalent = paymentAmount === null ? null : (frequency === 'biweekly' ? paymentAmount * 26 / 12 : paymentAmount)

      return {
        term,
        paymentAmount,
        numberOfPayments,
        totalBalance,
        programFeeAmount,
        monthCount,
        belowMinimum: onePercentMinimum > 0 && monthlyEquivalent !== null && monthlyEquivalent < onePercentMinimum,
      }
    })
  }, [config, totalDebt, frequency, startDate, onePercentMinimum])

  const selectedOption = termOptions.find(o => o.term === selectedTerm)

  return (
    <>
      <div className="border border-divider bg-surface shadow-lg rounded-2xl w-full">
        <div className="font-bold text-lg md:text-xl-20 pt-4 md:pt-6 pl-4 md:pl-12 pb-3 md:pb-4">
          Select Your Program Term
        </div>
        <div className="border-b border-divider mx-4 md:mx-6" />
        <div className="px-4 md:px-12 py-4 md:py-6">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {termOptions.map(option => (
              <div
                key={option.term}
                onClick={() => setSelectedTerm(option.term)}
                className={`border-2 rounded-lg p-4 md:p-6 cursor-pointer transition-all
                  ${selectedTerm === option.term
                    ? 'border-primary-main bg-[color-mix(in_srgb,var(--color-primary-main)_5%,transparent)]'
                    : 'border-divider hover:border-text-secondary'
                  }`}
              >
                <div className="text-center">
                  <div className="text-xl md:text-2xl font-bold text-primary-main">
                    {option.term} months
                  </div>
                  <div className="text-sm text-text-secondary mt-2">
                    {option.paymentAmount === null
                      ? `— × ${option.numberOfPayments}`
                      : `~$${option.paymentAmount.toFixed(2)} × ${option.numberOfPayments}`}
                  </div>
                  {option.belowMinimum && (
                    <div className="text-xs text-warning-main mt-1">
                      Below the ${formatMinimumPayment(onePercentMinimum)}/month minimum payment
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {selectedOption && (
        <div className="border border-divider bg-surface shadow-lg rounded-2xl w-full mt-4 md:mt-6">
          <div className="font-bold text-lg md:text-xl-20 pt-4 md:pt-6 pl-4 md:pl-12 pb-3 md:pb-4">
            Payment Summary
          </div>
          <div className="border-b border-divider mx-4 md:mx-6" />
          <div className="px-4 md:px-12 py-4 md:py-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 md:gap-4 text-sm">
              <div className="flex justify-between py-2 border-b border-divider">
                <span className="text-text-secondary">Total Debt:</span>
                <span className="font-medium text-text-primary">${amountDecimals ? formatLocaleAmount(totalDebt) : totalDebt.toLocaleString()}</span>
              </div>
              <div className="flex justify-between py-2 border-b border-divider">
                <span className="text-text-secondary">Program Fee ({config.programFee}%):</span>
                <span className="font-medium text-text-primary">${selectedOption.programFeeAmount.toFixed(2)}</span>
              </div>
              <div className="flex justify-between py-2 border-b border-divider">
                <span className="text-text-secondary">Program Length:</span>
                <span className="font-medium text-text-primary">{selectedOption.term} months ({selectedOption.numberOfPayments} payments)</span>
              </div>
              <div className="flex justify-between py-2 border-b border-divider">
                <span className="text-text-secondary">{frequency === 'biweekly' ? 'Bi-Weekly' : 'Monthly'} Payment:</span>
                <span className="font-medium text-primary-main text-base md:text-lg">
                  {selectedOption.paymentAmount === null ? '—' : `$${selectedOption.paymentAmount.toFixed(2)}`}
                </span>
              </div>
              {config.initialFee > 0 && (
                <div className="flex justify-between py-2 border-b border-divider">
                  <span className="text-text-secondary">Initial Fee:</span>
                  <span className="font-medium text-text-primary">${config.initialFee}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <div className="border border-divider bg-surface shadow-lg rounded-2xl w-full mt-4 md:mt-6">
        <div className="font-bold text-lg md:text-xl-20 pt-4 md:pt-6 pl-4 md:pl-12 pb-3 md:pb-4">
          Payment Schedule
        </div>
        <div className="border-b border-divider mx-4 md:mx-6" />
        <div className="px-4 md:px-12 py-4 md:py-6">
          <div className="w-full mb-4 md:mb-6">
            <Dropdown
              label="Payment Frequency"
              value={frequency}
              setValue={setFrequency}
              menuItems={frequencyOptions}
              placeholder="Select frequency"
            />
          </div>
          <div className="w-full">
            <Calendar
              optional={true}
              label="Start Date"
              value={startDate || null}
              onChange={(newValue) => setStartDate(newValue ? newValue.format('YYYY-MM-DD') : null)}
              disablePast={true}
            />
          </div>
        </div>
      </div>

      <div className="mt-4 md:mt-6 mb-6 flex flex-col-reverse sm:flex-row justify-between gap-3 w-full">
        <Button type="outlined" width="w-full sm:min-w-[156px] sm:w-auto" onClick={onBack}>
          Back
        </Button>
        <Button width="w-full sm:min-w-[156px] sm:w-auto" onClick={onNext} disabled={!selectedTerm}>
          Save & proceed
        </Button>
      </div>
    </>
  )
}

export default TermSelectionStep
